import { useEffect, useState } from "react";
import client, { getErrorMessage } from "@/api/client";
import { useSitesMap } from "@/hooks/useSitesMap";
import { useAuth } from "@/context/AuthContext";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import AssetDetailPanel from "./AssetDetailPanel";
import AssetFormDialog from "./AssetFormDialog";
import { toast } from "sonner";
import { Search, Plus } from "lucide-react";
import { MachineTypeFilter, useMachineTypeFilter } from "@/components/MachineTypeFilter";

const ASSET_ROLES = ["Director", "Manager", "Accounts", "Back Office"];

export default function AssetListPage() {
  const { user } = useAuth();
  const [assets, setAssets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("Active");
  const [machineType, setMachineType] = useMachineTypeFilter();
  const [selected, setSelected] = useState(null);
  const [formTarget, setFormTarget] = useState(null);
  const { sites, siteMap } = useSitesMap();
  const canManage = ASSET_ROLES.includes(user?.role);

  useEffect(() => {
    let active = true;
    setLoading(true);
    const params = { include_inactive: statusFilter === "All" };
    if (search) params.search = search;
    if (machineType) params.machine_type = machineType;
    client
      .get("/assets", { params })
      .then((res) => {
        if (active) setAssets(res.data);
      })
      .catch((err) => toast.error(getErrorMessage(err)))
      .finally(() => active && setLoading(false));
    return () => {
      active = false;
    };
  }, [search, statusFilter, machineType]);

  const handleSaved = (data) => {
    setFormTarget(null);
    setAssets((prev) => {
      const exists = prev.some((a) => a.id === data.id);
      return exists ? prev.map((a) => (a.id === data.id ? data : a)) : [data, ...prev];
    });
    setSelected(data);
  };

  return (
    <div className="space-y-6" data-testid="asset-register-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold tracking-tight text-slate-900" style={{ fontFamily: "Outfit, sans-serif" }}>
            Asset Register
          </h1>
          <p className="text-sm text-slate-500" data-testid="asset-count">
            {assets.length} asset record{assets.length === 1 ? "" : "s"}
          </p>
        </div>
        {canManage && (
          <Button data-testid="asset-add-button" onClick={() => setFormTarget("new")} className="bg-amber-600 hover:bg-amber-700">
            <Plus className="h-4 w-4 mr-1.5" /> Add Asset
          </Button>
        )}
      </div>

      <div className="flex flex-wrap items-center gap-3">
        <div className="relative w-full max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
          <Input
            data-testid="asset-search-input"
            placeholder="Search by Asset ID, model, machine type or brand"
            className="pl-9"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
        <MachineTypeFilter value={machineType} onChange={setMachineType} testId="asset-machine-type-filter" />
        <div className="flex gap-2">
          {["Active", "All"].map((s) => (
            <button
              key={s}
              data-testid={`asset-status-filter-${s.toLowerCase()}`}
              onClick={() => setStatusFilter(s)}
              className={`rounded-full px-3 py-1 text-xs font-semibold transition-colors ${
                statusFilter === s ? "bg-amber-600 text-white" : "bg-slate-100 text-slate-600 hover:bg-slate-200"
              }`}
            >
              {s}
            </button>
          ))}
        </div>
      </div>

      <div className="rounded-lg border border-slate-200 bg-white overflow-hidden">
        <Table data-testid="asset-table">
          <TableHeader>
            <TableRow>
              <TableHead>Asset ID</TableHead>
              <TableHead>Machine Type</TableHead>
              <TableHead>Brand</TableHead>
              <TableHead>Capacity</TableHead>
              <TableHead>Assigned Site</TableHead>
              <TableHead>Owned By</TableHead>
              <TableHead>Status</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={7} className="text-center py-8 text-slate-400">
                  Loading...
                </TableCell>
              </TableRow>
            ) : assets.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7} className="text-center py-8 text-slate-400">
                  No assets found.
                </TableCell>
              </TableRow>
            ) : (
              assets.map((a) => (
                <TableRow key={a.id} data-testid={`asset-row-${a.asset_id}`} className="cursor-pointer" onClick={() => setSelected(a)}>
                  <TableCell className="font-mono text-xs font-semibold">{a.asset_id}</TableCell>
                  <TableCell className="font-medium">{a.machine_type}</TableCell>
                  <TableCell>{a.brand}</TableCell>
                  <TableCell>{a.capacity || "—"}</TableCell>
                  <TableCell>{siteMap[a.assigned_site] || a.assigned_site || "—"}</TableCell>
                  <TableCell>{a.owned_by_entity}</TableCell>
                  <TableCell>
                    <Badge className={a.active === false ? "bg-slate-400" : a.current_status === "Active" ? "bg-emerald-600" : "bg-amber-500"}>
                      {a.active === false ? "Deactivated" : a.current_status}
                    </Badge>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>

      {selected && (
        <AssetDetailPanel
          asset={selected}
          siteMap={siteMap}
          onClose={() => setSelected(null)}
          canManage={canManage}
          onEdit={(a) => {
            setSelected(null);
            setFormTarget(a);
          }}
          onUpdated={(updated) => {
            setSelected(updated);
            setAssets((prev) => prev.map((a) => (a.id === updated.id ? updated : a)));
          }}
        />
      )}

      {formTarget && (
        <AssetFormDialog asset={formTarget === "new" ? null : formTarget} sites={sites} onClose={() => setFormTarget(null)} onSaved={handleSaved} />
      )}
    </div>
  );
}
